import { useMutation, useQuery } from '@apollo/client'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Rating,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { graphql } from '@/generated/gql'
import moment from 'moment'
import React, { useState } from 'react'
import { Close } from '@mui/icons-material'

const canteenItemRatingOrdersQuery = graphql(`
  query CanteenItemRating_OrdersQuery($input: CCUserOrderQueryInput!) {
    me {
      ccOrders(input: $input) {
        orders {
          date
          meal
          items {
            name
            id
            type
            ratedAt
            vendor {
              id
              companyName
            }
          }
        }
      }
    }
  }
`)

const canteenItemRatingRateItemMutation = graphql(`
  mutation Mutation($input: RateCCOrderItemInput!) {
    rateCCOrderItem(input: $input)
  }
`)

const ItemRating: React.FC = () => {
  const maxRating = 5
  const [closedByUser, setClosedByUser] = useState(false)
  const [periodStart] = useState(moment().subtract(1, 'week').toISOString())
  const [periodEnd] = useState(moment().subtract(1, 'day').toISOString())
  const [ratingValue, setRatingValue] = useState(0)
  const [moreDetails, setMoreDetails] = useState('')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { data } = useQuery(canteenItemRatingOrdersQuery, {
    variables: {
      input: {
        offset: 0,
        limit: 1,
        periodStart,
        periodEnd,
      },
    },
  })

  const [runSaveRatingMutation, { loading: savingRating }] = useMutation(
    canteenItemRatingRateItemMutation,
  )

  const saveRating = (input: { itemId: number; vendorId: number; skip?: boolean }) => {
    runSaveRatingMutation({
      variables: {
        input: {
          ccOrderItemId: input.itemId,
          maxRating,
          ...(moreDetails && { feedback: moreDetails }),
          rating: ratingValue,
          vendorId: input.vendorId,
          skipped: Boolean(input.skip),
        },
      },
    })
  }

  const handleClose = (input: { itemId: number; vendorId: number }) => {
    saveRating({ ...input, skip: true })
    setClosedByUser(true)
  }

  const handleSubmitRating = (input: { itemId: number; vendorId: number }) => {
    saveRating(input)
    setClosedByUser(true)
  }

  const orderData = data?.me.ccOrders.orders[0]

  const itemToReview = orderData?.items.reduce((foundItem, currentItem) => {
    const priorityMap: Record<string, number> = {
      main: 3,
      side: 2,
      drink: 1,
      '': 0,
    }
    if (foundItem.ratedAt) return foundItem
    if (!currentItem?.type) return foundItem
    const currentItemType = currentItem.type
    const foundItemType = foundItem?.type ?? ''
    const currentItemPriority = priorityMap[currentItemType]
    const foundItemPriority = priorityMap[foundItemType]
    if (currentItemPriority > foundItemPriority) return currentItem
    return foundItem
  })

  const saveInput = {
    itemId: Number(itemToReview?.id),
    vendorId: Number(itemToReview?.vendor.id),
  }

  return (
    <Dialog
      onClose={() => handleClose(saveInput)}
      fullWidth
      fullScreen={isMobile}
      maxWidth="sm"
      open={Boolean(itemToReview && !itemToReview.ratedAt && !closedByUser)}
    >
      <DialogTitle display="flex" alignItems="center">
        Rate your {moment(orderData?.date).format('dddd')} {orderData?.meal}
        <IconButton sx={{ marginLeft: 'auto' }} onClick={() => handleClose(saveInput)}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={4} width="100%" direction="column">
          <Typography variant="body1">
            How was your {itemToReview?.name} from {itemToReview?.vendor.companyName}?
          </Typography>
          <Rating
            onChange={(_, newRating) => {
              if (!newRating) return
              setRatingValue(newRating)
            }}
            value={ratingValue}
            size="large"
            max={maxRating}
            data-cy="canteen-item-rating-stars"
          />
          <TextField
            placeholder="Tell us more. Tasted fantastic? Right portion size? Wasn't what you were expecting?"
            label="Tell us more"
            multiline
            fullWidth
            minRows={5}
            value={moreDetails}
            onChange={(e) => setMoreDetails(e.target.value)}
            data-cy="canteen-item-rating-textarea"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => handleSubmitRating(saveInput)}
          disabled={ratingValue === 0 || savingRating}
          data-cy="canteen-item-rating-submit"
        >
          Submit rating
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ItemRating
